import React from 'react';
import { LogOut, X, AlertTriangle } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';

const LogoutConfirmationModal = ({ isOpen, onConfirm, onCancel, ociMode }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/60' : 'bg-gray-900/50'} backdrop-blur-sm flex items-center justify-center p-4 z-50`}>
      <div className="relative group/modal">
        {/* Liquid Glass Modal Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40 backdrop-blur-2xl border border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.6)]'
          : 'glass-light-card shadow-[0_8px_32px_rgba(15,23,42,0.12)]'
        } rounded-3xl`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>


        <div className="relative w-full max-w-md p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              {/* Warning Icon */}
              <div className="relative group/icon">
                <div className={`absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                <div className={`relative ${isDarkMode
                  ? 'bg-gradient-to-br from-amber-600/60 via-amber-700/50 to-amber-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(245,158,11,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                  : 'bg-amber-50'
                } w-12 h-12 rounded-2xl flex items-center justify-center`}>
                  <AlertTriangle className={`w-6 h-6 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-amber-600'}`} />
                  <div className={`absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                </div>
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode
                  ? 'bg-gradient-to-r from-white via-amber-200 to-white bg-clip-text text-transparent drop-shadow-lg'
                  : 'text-slate-900'
                }`}>
                  {ociMode ? t('logout.titleSap') : t('logout.titleLogout')}
                </h2>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={onCancel}
              className="relative group/close overflow-hidden rounded-xl transition-all duration-300 transform hover:scale-[1.1] active:scale-[0.9]"
            >
              <div
                className="absolute inset-0 backdrop-blur-xl rounded-xl"
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)'
                    : undefined,
                  backgroundColor: isDarkMode ? undefined : undefined,
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : undefined,
                  boxShadow: isDarkMode
                    ? '0 4px 16px rgba(0,0,0,0.3)'
                    : undefined
                }}
              ></div>
              {!isDarkMode && (
                <div className="absolute inset-0 bg-slate-100 hover:bg-slate-200 rounded-xl border border-slate-200"></div>
              )}

              <div className={`relative z-10 w-8 h-8 flex items-center justify-center ${isDarkMode ? 'text-white/80 drop-shadow-lg' : 'text-slate-600'}`}>
                <X className="w-4 h-4 group-hover/close:scale-110 transition-transform duration-300" />
              </div>

            </button>
          </div>

          {/* Content */}
          <div className="mb-8">
            <p className={`${isDarkMode ? 'text-white/80 drop-shadow-sm' : 'text-slate-600'} text-center`}>
              {ociMode
                ? t('logout.confirmSap')
                : t('logout.confirmLogout')
              }
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            {/* Cancel Button */}
            <button
              onClick={onCancel}
              className={`relative group/cancel overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] flex-1 ${isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 border border-slate-200'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : undefined}
            >
              {/* Smooth Hover Overlay */}
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/cancel:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                } : undefined}
              ></div>

              <div className={`relative z-10 py-3 px-4 flex items-center justify-center font-medium transition-all duration-300 ${
                isDarkMode ? 'text-white drop-shadow-lg group-hover/cancel:drop-shadow-xl' : 'text-slate-700'
              }`}>
                {t('logout.cancel')}
              </div>

              {/* Smooth Wave Effect */}
              <div className={`absolute inset-0 opacity-0 group-hover/cancel:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/cancel:translate-x-full transition-transform duration-800"
                  style={{
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                  }}
                ></div>
              </div>
            </button>

            {/* Confirm Button */}
            <button
              onClick={onConfirm}
              className={`relative group/confirm overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] flex-1 ${!isDarkMode ? 'bg-[#dc2626] hover:bg-[#b91c1c]' : ''}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(220,38,38,0.7) 0%, rgba(185,28,28,0.7) 50%, rgba(153,27,27,0.7) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(239,68,68,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {
                border: 'none',
                boxShadow: '0 2px 8px rgba(220,38,38,0.25)'
              }}
            >
              {/* Smooth Hover Overlay */}
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/confirm:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                } : undefined}
              ></div>

              <div className="relative z-10 py-3 px-4 flex items-center justify-center font-medium text-white transition-all duration-300 drop-shadow-sm">
                <LogOut className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/confirm:scale-110" />
                {ociMode ? t('logout.toSap') : t('logout.submit')}
              </div>

              {/* Smooth Wave Effect */}
              <div className={`absolute inset-0 opacity-0 group-hover/confirm:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/confirm:translate-x-full transition-transform duration-800"
                  style={{
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                  }}
                ></div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoutConfirmationModal;
